import axios, { AxiosResponse, AxiosRequestConfig } from 'axios';
import BootstrapVue from 'bootstrap-vue';
import Vue from 'vue';
import App from './App.vue';
import i18n from './internationalization';
import routeMapping from './route-mapping';
import router from './router';
import AppInsights from './Shared/AppInsights';
import WaitIndicatorDirective from './Shared/WaitIndicatorDirective';
import store, { typedStore } from './store';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import './styles.scss';
import { formatDate, splitCamelCase } from './Shared/Filters';
import ViewPort from './Shared/ViewPort.vue';
import './fa-config.ts';
import TypedStorePlugin from '@/Stores/TypedStorePlugin';
import DialectSafeNumberFormatter from '@/internationalization/DialectSafeNumberFormatter';
import VCalendar from 'v-calendar';
import 'v-calendar/lib/v-calendar.min.css';
import { SeverityLevel, IExceptionTelemetry } from '@microsoft/applicationinsights-common';
import LocationLookupViewer from './Shared/LocationLookupViewer.vue';
import LocationLookupDropdown from './Shared/LocationLookupDropdown.vue';
import { stringCompare } from './Shared/Utilities';
import { onResponseRejectedRerouteUnauthorizedRequest } from '@/interceptor-methods';
import VueCurrencyInput from 'vue-currency-input';

Vue.config.devtools = true;

AppInsights.initializeAppInsights();

// Causes .NET to return 401 instead of a redirect on auth failure
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Sets culture for .NET
document.cookie = `.AspNetCore.Culture=c=${i18n.locale}|uic=${i18n.locale};secure;path=/`;

axios.interceptors.request.use(
    (config) => {
        store.commit('loaderModule/startLoading', 'main.ts interceptor');
        return config;
    },
    (error) => {
        const request = error.request as AxiosRequestConfig;
        store.commit('loaderModule/finishLoading', 'main.ts interceptor');
        return Promise.reject(error);
    });

axios.interceptors.response.use((response) => {
    store.commit('loaderModule/finishLoading', 'main.ts interceptor');
    return response;

}, (error) => {
    store.commit('loaderModule/finishLoading', 'main.ts interceptor');
    return onResponseRejectedRerouteUnauthorizedRequest(error);
});

Vue.config.productionTip = false;
Vue.use(VCalendar as any, {
    locale: i18n.locale,
    popoverVisibility: 'focus'
});
Vue.use(BootstrapVue);
Vue.use(TypedStorePlugin);
Vue.use(DialectSafeNumberFormatter);

const pluginOptions = {
    /* see config reference */
    globalOptions: { currency: 'USD', locale: 'en' }
};
Vue.use(VueCurrencyInput, pluginOptions);


Vue.config.errorHandler = (err, vm, info) => {
    if (process.env.NODE_ENV !== 'production') {
        const { warn }: any = (Vue as any).util;
        warn(`Error in ${info}: "${err.toString()}"`, vm);
    }

    // tslint:disable-next-line:no-console
    console.error(err);

    if (window.appInsights) {
        const exception: IExceptionTelemetry = {
            exception: err,
            severityLevel: SeverityLevel.Critical
        };
        window.appInsights.trackException(exception);
    }
};

Vue.use(BootstrapVue);
Vue.component('font-awesome-icon', FontAwesomeIcon);
Vue.component('LocationLookupDropdown', LocationLookupDropdown);
Vue.component('LocationLookupViewer', LocationLookupViewer);
Vue.component('viewport', ViewPort);

Vue.directive('wait-indicator', WaitIndicatorDirective);

Vue.filter('formatDate', formatDate);
Vue.filter('splitCamelCase', splitCamelCase);

const app = new Vue({
    router,
    store,
    i18n,
    render: (h) => h(App),
});

routeMapping.mapAndReroute(router)
    .then(() => {
        const location = router.currentRoute.params.location;
        if (location) {
            return typedStore.modules.userModule.actions.getAllowedLocations().then(locations => {
                const currentLocation = locations.find(item => stringCompare(item.urlSegment, location));
                return typedStore.actions.changeCurrentLocation(currentLocation!);
            });
        }
    })
    .then(() => {
        app.$mount('#app');
    });

